import React, { useState, useEffect } from 'react';
import {
  GraduationCap,
  Calendar,
  CheckCircle,
  Clock,
  Trophy,
  Users,
  FileText,
  BarChart3,
  TrendingUp,
  Award,
  BookOpen,
  Target,
  Activity,
  PieChart as PieChartIcon
} from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Area, AreaChart, Legend } from 'recharts';
import api from '../api';
import { toast } from 'react-toastify';

const SoutenanceDashboard = ({ onDataChange }) => {
  const [statistics, setStatistics] = useState({});
  const [pvStats, setPvStats] = useState({});
  const [recentSoutenances, setRecentSoutenances] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Récupérer les statistiques générales
      const [statsResponse, pvStatsResponse, soutenancesResponse] = await Promise.all([
        api.get('/soutenance/statistiques/'),
        api.get('/soutenance/proces-verbaux/stats/'),
        api.get('/soutenance/soutenances/?page=1&page_size=8')
      ]);

      console.log('Statistiques reçues:', statsResponse.data);
      console.log('Types de soutenance:', statsResponse.data?.data?.repartition_types);

      if (statsResponse.data.status === 'success') {
        setStatistics(statsResponse.data.data);
        console.log('Statistiques définies:', statsResponse.data.data);
        console.log('Répartition types:', statsResponse.data.data?.repartition_types);
      }

      if (pvStatsResponse.data.status === 'success') {
        setPvStats(pvStatsResponse.data.data);
      }

      if (soutenancesResponse.data.status === 'success') {
        setRecentSoutenances(soutenancesResponse.data.data || []);
      }

    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      toast.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'];

  // Données pour les graphiques
  const statutData = statistics.repartition_statuts ? Object.entries(statistics.repartition_statuts).map(([key, value], index) => ({
    name: getStatutLabel(key),
    value: value,
    color: COLORS[index % COLORS.length]
  })).filter(item => item.value > 0) : [];

  const mentionData = statistics.repartition_mentions ? Object.entries(statistics.repartition_mentions).map(([key, value], index) => ({
    name: getMentionLabel(key),
    value: value,
    color: COLORS[index % COLORS.length]
  })).filter(item => item.value > 0) : [];

  const decisionData = statistics.repartition_decisions ? Object.entries(statistics.repartition_decisions).map(([key, value], index) => ({
    name: getDecisionLabel(key),
    value: value,
    color: COLORS[index % COLORS.length]
  })).filter(item => item.value > 0) : [];

  const typeData = statistics.repartition_types && Object.keys(statistics.repartition_types).length > 0 
    ? Object.entries(statistics.repartition_types).map(([key, value], index) => ({
        name: key || 'Non défini',
        value: value,
        color: COLORS[index % COLORS.length]
      })).filter(item => item.value > 0)
    : [];

  // Vérifier s'il y a des vraies données
  const hasRealTypeData = statistics.repartition_types && Object.keys(statistics.repartition_types).length > 0 && 
                         Object.values(statistics.repartition_types).some(value => value > 0);
  const hasRealStatutData = statistics.repartition_statuts && Object.keys(statistics.repartition_statuts).length > 0 && 
                           Object.values(statistics.repartition_statuts).some(value => value > 0);
  const hasRealMentionData = statistics.repartition_mentions && Object.keys(statistics.repartition_mentions).length > 0 && 
                            Object.values(statistics.repartition_mentions).some(value => value > 0);
  const hasRealDecisionData = statistics.repartition_decisions && Object.keys(statistics.repartition_decisions).length > 0 && 
                             Object.values(statistics.repartition_decisions).some(value => value > 0);
  
  // Utiliser les vraies données ou des tableaux vides
  const finalStatutData = hasRealStatutData ? statutData : [];
  const finalMentionData = hasRealMentionData ? mentionData : [];
  const finalDecisionData = hasRealDecisionData ? decisionData : [];
  const finalTypeData = hasRealTypeData ? typeData : [];

  console.log('=== DEBUG TYPES DE SOUTENANCE ===');
  console.log('Has real type data:', hasRealTypeData);
  console.log('Type data final:', finalTypeData);
  console.log('Statistics repartition_types:', statistics.repartition_types);
  console.log('TypeData length:', finalTypeData.length);
  console.log('=== FIN DEBUG ===');

  function getStatutLabel(statut) {
    const labels = {
      'PLANIFIEE': 'Planifiées',
      'EN_COURS': 'En cours',
      'TERMINEE': 'Terminées',
      'ANNULEE': 'Annulées'
    };
    return labels[statut] || statut;
  }

  function getMentionLabel(mention) {
    const labels = {
      'PASSABLE': 'Passable',
      'ASSEZ_BIEN': 'Assez bien',
      'BIEN': 'Bien',
      'TRES_BIEN': 'Très bien',
      'EXCELLENT': 'Excellent'
    };
    return labels[mention] || mention;
  }

  function getDecisionLabel(decision) {
    const labels = {
      'ADMIS': 'Admis',
      'ADMIS_RESERVE': 'Admis avec réserve',
      'AJOURNE': 'Ajourné',
      'REPORTE': 'Reporté'
    };
    return labels[decision] || decision;
  }

  function getEtudiantNames(soutenance) {
    // Essayer d'abord avec etudiants_soutenance (structure complète)
    if (soutenance.etudiants_soutenance && soutenance.etudiants_soutenance.length > 0) {
      return soutenance.etudiants_soutenance.map(es => 
        `${es.etudiant.nom} ${es.etudiant.prenom}`
      ).join(', ');
    }
    
    // Ensuite avec etudiant_principal_info (structure simplifiée)
    if (soutenance.etudiant_principal_info) {
      return `${soutenance.etudiant_principal_info.nom} ${soutenance.etudiant_principal_info.prenom}`;
    }
    
    // Enfin, essayer avec l'ancienne structure etudiant (pour compatibilité)
    if (soutenance.etudiant) {
      return `${soutenance.etudiant.nom} ${soutenance.etudiant.prenom}`;
    }
    
    return 'Étudiant non défini';
  }

  if (loading) {
    return (
      <div className="p-8 animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-gray-200 h-32 rounded-xl"></div>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-gray-200 h-80 rounded-xl"></div>
          <div className="bg-gray-200 h-80 rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 bg-gray-50 min-h-screen">
      {/* En-tête */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Tableau de bord des soutenances</h1>
        <p className="text-gray-600">Vue d'ensemble des activités de soutenance et procès-verbaux</p>
      </div>

      {/* Cartes de statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total Soutenances</p>
              <p className="text-3xl font-bold">{statistics.total_soutenances || 0}</p>
              <p className="text-blue-200 text-xs mt-1">Cette année</p>
            </div>
            <GraduationCap className="h-14 w-14 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Terminées</p>
              <p className="text-3xl font-bold">{statistics.soutenances_terminees || 0}</p>
              <p className="text-green-200 text-xs mt-1">Soutenances finalisées</p>
            </div>
            <CheckCircle className="h-14 w-14 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Procès-verbaux</p>
              <p className="text-3xl font-bold">{statistics.total_proces_verbaux || 0}</p>
              <p className="text-orange-200 text-xs mt-1">{statistics.taux_completion_pv || 0}% de completion</p>
            </div>
            <FileText className="h-14 w-14 text-orange-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Moyenne Générale</p>
              <p className="text-3xl font-bold">{statistics.moyenne_notes ? `${statistics.moyenne_notes}/20` : '0/20'}</p>
              <p className="text-purple-200 text-xs mt-1">{statistics.taux_reussite || 0}% de réussite</p>
            </div>
            <Trophy className="h-14 w-14 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Cartes de statistiques secondaires */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">En Cours</h3>
            <Clock className="h-8 w-8 text-blue-500" />
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600 mb-2">
              {statistics.soutenances_en_cours || 0}
            </div>
            <p className="text-gray-600 text-sm">Soutenances actives</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">PV Validés</h3>
            <Award className="h-8 w-8 text-green-500" />
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">
              {statistics.pv_valides || 0}
            </div>
            <p className="text-gray-600 text-sm">{pvStats.taux_validation || 0}% validés</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Types Actifs</h3>
            <BookOpen className="h-8 w-8 text-indigo-500" />
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-indigo-600 mb-2">
              {Object.keys(statistics.repartition_types || {}).filter(key => (statistics.repartition_types || {})[key] > 0).length}
            </div>
            <p className="text-gray-600 text-sm">Types de soutenance</p>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Planifiées</h3>
            <Calendar className="h-8 w-8 text-orange-500" />
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600 mb-2">
              {statistics.soutenances_planifiees || 0}
            </div>
            <p className="text-gray-600 text-sm">À venir</p>
          </div>
        </div>
      </div>

      {/* Graphiques principaux */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Répartition par statut */}
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Répartition par Statut</h3>
            <PieChartIcon className="h-6 w-6 text-gray-500" />
          </div>
          {finalStatutData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={finalStatutData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {finalStatutData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value, entry) => `${value}: ${entry.payload.value}`}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-80 text-gray-500">
              <div className="text-center">
                <PieChartIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Aucune donnée disponible</p>
                <p className="text-sm">Aucune soutenance n'a encore été créée</p>
              </div>
            </div>
          )}
        </div>

        {/* Répartition des mentions */}
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Répartition des Mentions</h3>
            <Trophy className="h-6 w-6 text-gray-500" />
          </div>
          {finalMentionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={finalMentionData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]} name="Nombre d'étudiants" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-80 text-gray-500">
              <div className="text-center">
                <Trophy className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Aucune mention disponible</p>
                <p className="text-sm">Aucun procès-verbal n'a encore été validé</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Graphiques secondaires */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Répartition des décisions */}
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Décisions du Jury</h3>
            <Target className="h-6 w-6 text-gray-500" />
          </div>
          {finalDecisionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={finalDecisionData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${(percent * 100).toFixed(1)}%`}
                >
                  {finalDecisionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  formatter={(value, entry) => `${value}: ${entry.payload.value}`}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-80 text-gray-500">
              <div className="text-center">
                <Target className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Aucune décision disponible</p>
                <p className="text-sm">Aucun procès-verbal n'a encore été validé</p>
              </div>
            </div>
          )}
        </div>

        {/* Répartition par type */}
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Types de Soutenance</h3>
            <BarChart3 className="h-6 w-6 text-gray-500" />
          </div>
          
          {finalTypeData.length > 0 ? (
            <div style={{ width: '100%', height: 320, display: 'flex', justifyContent: 'center' }}>
              <BarChart 
                width={500}
                height={300}
                data={finalTypeData}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#10B981" />
              </BarChart>
            </div>
          ) : (
            <div className="flex items-center justify-center h-80 text-gray-500">
              <div className="text-center">
                <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Aucun type de soutenance</p>
                <p className="text-sm">Aucune soutenance n'a encore été créée</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tableau des soutenances récentes */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-900">Soutenances Récentes</h3>
            <Activity className="h-6 w-6 text-gray-500" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Étudiant(s)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Titre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Note
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentSoutenances.length > 0 ? recentSoutenances.map((soutenance) => (
                <tr key={soutenance.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {getEtudiantNames(soutenance)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 max-w-xs truncate" title={soutenance.titre_travail}>
                      {soutenance.titre_travail}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {soutenance.type_soutenance?.nom || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(soutenance.date_soutenance).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      soutenance.statut === 'TERMINEE' ? 'bg-green-100 text-green-800' :
                      soutenance.statut === 'PLANIFIEE' ? 'bg-blue-100 text-blue-800' :
                      soutenance.statut === 'EN_COURS' ? 'bg-yellow-100 text-yellow-800' :
                      soutenance.statut === 'ANNULEE' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {getStatutLabel(soutenance.statut)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {soutenance.note_finale ? (
                      <span className={`font-semibold ${
                        soutenance.note_finale >= 16 ? 'text-green-600' :
                        soutenance.note_finale >= 14 ? 'text-blue-600' :
                        soutenance.note_finale >= 12 ? 'text-yellow-600' :
                        soutenance.note_finale >= 10 ? 'text-orange-600' :
                        'text-red-600'
                      }`}>
                        {soutenance.note_finale}/20
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <BookOpen className="h-12 w-12 text-gray-300 mb-2" />
                      <p>Aucune soutenance récente</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SoutenanceDashboard; 